import { PortableText } from '@portabletext/react';
import { graphql, useStaticQuery } from 'gatsby';
import groq from 'groq';
import React from 'react';
import { ALL_LANGS } from '../../constants';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../graphql-fragments/SanityImage';
import { NavigationMenu } from '../../graphql-fragments/navigationMenu';
import { getComponents } from '../../portableText';
import PreviewLoadingScreen from '../../preview/PreviewLoadingScreen';
import { usePreviewData } from '../../preview/previewUtils';
import { BackgroundColorTheme } from '../../templates/Page';
import i18n from '../../translation/i18n';
import { RawPortableText } from '../../types/types';
import { localize } from '../../utils/sanity';
import { NavigationBlock, NewsletterBlock, SocialNetworksBlock } from './Footer';
import Layout from './Layout';

interface SanityLayoutProps {
  children: React.ReactNode;
  pageTheme?: BackgroundColorTheme;
  lastModuleTheme?: BackgroundColorTheme;
  frontCloudPosition?: 'fullScreen' | 'top' | 'bottom';
  hideCookieBar?: boolean;
}

interface LocalizedQueryData {
  sanityWebsite: {
    logo: SanityImageType;
    whiteLogo: SanityImageType;
    blackLogo: SanityImageType;
    header: {
      navigationMenu: NavigationMenu;
      ctaButtons: Array<ButtonLinkType>;
    };
    footer: {
      socialMediaBlock?: {
        title?: string;
        facebookUrl?: string;
        instagramUrl?: string;
        whatsappUrl?: string;
        linkedinUrl?: string;
      };
      navigationBlock?: NavigationBlock;
      policyLinks: Array<ButtonLinkType>;
      newsletterBlock?: NewsletterBlock;
    };
    cookiesBannerConfig?: {
      text: RawPortableText;
    };
    bookingKitFloatingWidgetEmbedCode?: string;
  };
}

const SanityLayout = ({
  children,
  lastModuleTheme,
  pageTheme,
  frontCloudPosition,
  hideCookieBar,
}: SanityLayoutProps): React.ReactElement => {
  const staticData = useStaticQuery<LocalizedQueryData>(graphql`
    {
      sanityWebsite {
        logo {
          ...SanityImage
        }
        whiteLogo {
          ...SanityImage
        }
        blackLogo {
          ...SanityImage
        }
        header {
          navigationMenu {
            ...NavigationMenu
          }
          ctaButtons {
            ...ButtonLink
          }
        }
        footer {
          socialMediaBlock {
            title {
              ...LocaleStringFragment
            }
            facebookUrl
            instagramUrl
            whatsappUrl
            linkedinUrl
          }
          navigationBlock {
            title {
              ...LocaleStringFragment
            }
            links {
              ...ButtonLink
            }
          }
          policyLinks {
            ...ButtonLink
          }
          newsletterBlock {
            title {
              ...LocaleStringFragment
            }
            buttonText {
              ...LocaleStringFragment
            }
          }
        }
        cookiesBannerConfig {
          text {
            ...LocaleParagraphPortableText
          }
        }
        bookingKitFloatingWidgetEmbedCode
      }
    }
  `);
  const groqQuery = groq`{
    "sanityWebsite": *[_type == "website"][0] {
      ...
    }
  }`;

  const data = usePreviewData<LocalizedQueryData>(staticData, {
    groqQuery,
  });

  if (!data) {
    return <PreviewLoadingScreen></PreviewLoadingScreen>;
  }

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(data, langs);

  const { navigationMenu, ctaButtons: headerCtaButtons } = localizedData.sanityWebsite.header;
  const { policyLinks, socialMediaBlock, navigationBlock, newsletterBlock } =
    localizedData.sanityWebsite.footer;
  const { logo, whiteLogo, blackLogo, cookiesBannerConfig, bookingKitFloatingWidgetEmbedCode } =
    localizedData.sanityWebsite;

  const socialNetwork: SocialNetworksBlock = {
    title: socialMediaBlock?.title,
    socialNetworks:
      socialMediaBlock?.facebookUrl ||
      socialMediaBlock?.instagramUrl ||
      socialMediaBlock?.linkedinUrl ||
      socialMediaBlock?.whatsappUrl
        ? [
            { iconName: 'facebook', url: socialMediaBlock.facebookUrl },
            { iconName: 'linkedin', url: socialMediaBlock.linkedinUrl },
            { iconName: 'whatsapp', url: socialMediaBlock.whatsappUrl },
            { iconName: 'instagram', url: socialMediaBlock.instagramUrl },
          ]
        : [],
  };
  return (
    <Layout
      pageTheme={pageTheme}
      frontCloudPosition={frontCloudPosition}
      headerConfig={{
        sticky: true,
        darkLogoImage: blackLogo,
        lightLogoImage: whiteLogo,
        entries: navigationMenu,
        legalLinks: policyLinks,
        ctaButtons: headerCtaButtons,
        theme: pageTheme,
      }}
      footerConfig={{
        logoImage:
          lastModuleTheme === 'starlit' || lastModuleTheme === 'moonlit' ? whiteLogo : blackLogo,
        socialNetworksBlock: socialNetwork,
        navigationBlock: navigationBlock,
        newsletterBlock: newsletterBlock,
        policyLinks: policyLinks,
        theme: lastModuleTheme,
      }}
      cookiesBannerConfig={{
        textContent: cookiesBannerConfig && (
          <div>
            <PortableText value={cookiesBannerConfig.text} components={getComponents()} />
          </div>
        ),
      }}
      hideCookieBar={hideCookieBar}
      floatingWidgetEmbedCode={bookingKitFloatingWidgetEmbedCode}
    >
      {children}
    </Layout>
  );
};

export default SanityLayout;
